import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { CloseIcon, CheckIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";
import { requests } from "../../shared/api";
import type { CatalogGrant } from "../../shared/api/requests";
import { log } from "../../logger";
import type { GrantLevel } from "@egalregal/shared";

const LEVELS: GrantLevel[] = ["read", "write", "admin"];

interface ShareDialogProps {
  entryId: string;
  /** The current grantee list; kept immutable — the dialog mutates via the API. */
  initialGrants: CatalogGrant[];
  onClose: () => void;
}

/**
 * Owner-only share dialog (ticket 07): lists current grantees with a level
 * select and revoke, plus an add-grantee input. Every change goes straight
 * through `PUT/DELETE /api/catalogs/:id/acl/:grantee` and reflects immediately.
 */
export function ShareDialog({ entryId, initialGrants, onClose }: ShareDialogProps) {
  const t = useT();
  const [grants, setGrants] = useState<CatalogGrant[]>(initialGrants);
  const [username, setUsername] = useState("");
  const [usernames, setUsernames] = useState<string[]>([]);
  const [level, setLevel] = useState<GrantLevel>("read");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    requests.catalogs
      .usernames()
      .then((names) => {
        if (!cancelled) setUsernames(names);
      })
      .catch((err) => log.warn({ err }, "Failed to load usernames for the share dialog"));
    return () => {
      cancelled = true;
    };
  }, []);

  const apply = async (action: () => Promise<{ acl: CatalogGrant[] }>) => {
    setBusy(true);
    setError(undefined);
    try {
      const { acl } = await action();
      setGrants(acl);
    } catch (err) {
      log.warn({ err, entryId }, "Failed to update share grants");
      setError(t("saveError", { error: t("loadError") }));
    } finally {
      setBusy(false);
    }
  };

  const addGrantee = async () => {
    const grantee = username.trim();
    if (!grantee || grants.some((g) => g.grantee === grantee)) return;
    await apply(() => requests.catalogs.setGrant(entryId, grantee, level));
    setUsername("");
  };

  const changeLevel = async (grantee: string, next: GrantLevel) => {
    await apply(() => requests.catalogs.setGrant(entryId, grantee, next));
  };

  const revoke = async (grantee: string) => {
    await apply(() => requests.catalogs.revokeGrant(entryId, grantee));
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-testid="share-dialog">
      <DialogTitle>{t("shareTitle")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("sharees")}
        </Typography>
        {grants.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("shareesEmpty")}
          </Typography>
        )}
        <Stack spacing={1} sx={{ mb: 2 }}>
          {grants.map((g) => (
            <Box key={g.grantee} data-testid="grant-row" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ flex: 1 }}>{g.grantee}</Typography>
              <Select
                size="small"
                value={g.level}
                disabled={busy}
                data-testid={`grant-level-${g.grantee}`}
                onChange={(e) => void changeLevel(g.grantee, e.target.value as GrantLevel)}
              >
                {LEVELS.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                size="small"
                aria-label={t("revoke")}
                disabled={busy}
                data-testid={`grant-revoke-${g.grantee}`}
                onClick={() => void revoke(g.grantee)}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ))}
        </Stack>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Autocomplete
            freeSolo
            size="small"
            options={usernames}
            inputValue={username}
            onInputChange={(_e, value) => setUsername(value)}
            sx={{ flex: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("username")}
                inputProps={{ ...params.inputProps, "data-testid": "share-grantee-input" }}
              />
            )}
          />
          <Select
            size="small"
            value={level}
            data-testid="share-level-select"
            onChange={(e) => setLevel(e.target.value as GrantLevel)}
          >
            {LEVELS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            size="small"
            disabled={busy || username.trim() === ""}
            onClick={() => void addGrantee()}
            startIcon={<CheckIcon />}
            data-testid="share-add-button"
          >
            {t("add")}
          </Button>
        </Box>
        {error && (
          <Typography color="error" variant="body2" role="alert" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
}